import { Dispatch, SetStateAction, useState } from 'react';
import { ResourceRowData } from '@features/components/CaseOverview/components/AssignedResources/types';
import { AssignedUser, CaseUserGroup } from '@/api/apis/case';
import { AssignDetachState, AssignDetachUsersState } from '@/utils/helpers/resources.helpers';
import { CustomerRole, CustomerRoleT } from '@/api/apis/customer';
import { EmployeeRole } from '@/api/apis/resources';
import { RoleKeyedPartial } from '@/types';

type CoordinatorStateProps = {
  assignedCustomers: Partial<Record<CaseUserGroup, AssignedUser[]>>;
  selectedRole: EmployeeRole | CustomerRoleT | undefined;
  pendingUsers: RoleKeyedPartial<AssignDetachState>;
  setPendingUsers: Dispatch<SetStateAction<RoleKeyedPartial<AssignDetachState>>>;
  assignedUsersByRoles: Record<string, string[]>;
  onUserAssign: (user: ResourceRowData) => void;
};

export function useTxUsersAssign({
  assignedCustomers = {},
  selectedRole,
  pendingUsers,
  setPendingUsers,
  assignedUsersByRoles,
  onUserAssign,
}: CoordinatorStateProps) {
  const coordinators = assignedCustomers[CaseUserGroup.CustomerCoordinators] ?? [];
  const hasCoordinator = coordinators.length > 0;
  const [serviceWithoutCoordinator, setServiceWithoutCoordinator] = useState(!hasCoordinator);
  const [prevPendingTxCoordinators, setPrevPendingTxCoordinators] = useState<AssignDetachUsersState | null>(null);

  const handleServiceWithoutCoordinatorChange = (val: boolean) => {
    setServiceWithoutCoordinator(val);
    if (val) {
      if (!prevPendingTxCoordinators)
        setPrevPendingTxCoordinators({
          [CustomerRole.TxCenterCoordinator]: pendingUsers[CustomerRole.TxCenterCoordinator],
        });

      const clearedPendingCoordinators: AssignDetachState = {
        assign: [],
        detach: assignedUsersByRoles[CustomerRole.TxCenterCoordinator] || [],
      };

      setPendingUsers((prev) => ({
        ...prev,
        [CustomerRole.TxCenterCoordinator]: clearedPendingCoordinators,
      }));
    } else if (prevPendingTxCoordinators) {
      setPendingUsers((prev) => ({
        ...prev,
        ...prevPendingTxCoordinators,
      }));
    }
  };

  const onTxAssign = (user: ResourceRowData) => {
    if (selectedRole === CustomerRole.TxCenterCoordinator) {
      const allCoordinators = [
        ...(assignedUsersByRoles[CustomerRole.TxCenterCoordinator] ?? []),
        ...(pendingUsers[CustomerRole.TxCenterCoordinator]?.assign ?? []),
      ];
      const uniqueCoordinators = Array.from(new Set(allCoordinators));
      setServiceWithoutCoordinator(!uniqueCoordinators.length);
    }
    onUserAssign(user);
  };

  return {
    coordinators,
    hasCoordinator,
    serviceWithoutCoordinator,
    setServiceWithoutCoordinator,
    prevPendingTxCoordinators,
    setPrevPendingTxCoordinators,
    handleServiceWithoutCoordinatorChange,
    onTxAssign,
  };
}
